import React, { useEffect, useRef, useState, useCallback } from 'react';
import Select from 'react-select';

import { viewAllSecurityQuestions } from '../../../../../services/SecurityQuestionsService';

import { ToolTip } from '../../../../common/tool-tip/ToolTip';
import Modal from '../../../../common/modal/Modal';
import { ClientRelations } from '../relations/ClientRelations';
import { Checkbox } from '../../../../common/checkbox/Checkbox';

import './ClientContactDetails.css';

export const ClientContactDetails = ({
  context,
  setContext,
  toolTipsEnabled,
  showToaster,
  setSpinnerText,
  clientContactDetails,
  clientContactDetailsErrors,
  handleClientContactDetailsChange,
  clientRelationInformation,
  setClientRelationInformation,
  clientRelationError,
  setClientRelationError,
  handleClientRelationInformationChange,
  handleClientRelationErrors,
  fieldsDisabled,
}) => {
  const [securityQuestionList, setSecurityQuestionList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [relation, setRelation] = useState({});

  // eslint-disable-next-line quotes
  const mobileNumberList = useRef(["Client's Own Number", "Someone Else's Number"]);
  // eslint-disable-next-line quotes
  const alternativeNumberList = useRef(["Client's Own Number", "Someone Else's Number"]);
  const discretionList = useRef([
    'Appointment',
    'Study Appointment',
    // eslint-disable-next-line quotes
    "Doctor's Appointment",
    'Aurum',
    'Friend',
    'Other',
  ]);

  const retrieveSecurityQuestions = useCallback(async () => {
    setSpinnerText('Retrieving Security Questions');
    const [data, error] = await viewAllSecurityQuestions(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        if (data.securityQuestions.length > 0) {
          setSecurityQuestionList(data.securityQuestions);
        }
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [context.username, setSpinnerText, showToaster]);

  useEffect(() => {
    retrieveSecurityQuestions();
  }, [retrieveSecurityQuestions]);

  const handleSecurityQuestionChange = (selectedSecurityOption) => {
    handleClientContactDetailsChange('selectedSecurityQuestion', selectedSecurityOption);
  };

  const handleMobileNumberOwnerChange = (selectedMobileOwnerOption) => {
    // eslint-disable-next-line quotes
    if (selectedMobileOwnerOption.label === "Someone Else's Number") {
      setRelation({
        relationMobile: clientContactDetails.mobileNumber,
      });
      setClientRelationInformation({
        ...clientRelationInformation,
        relationFirstName: '',
        relationLastName: '',
        relationship: '',
        relationMobile: clientContactDetails?.mobileNumber || '',
        relationMobileNumberVerified: false,
        selectedDiscretion: '',
        otherSpecify: '',
        relationEmail: '',
        emailVerified: false,
        isClientMinor: false,
        isLegalGuardian: false,
        isMainContact: false,
      });

      setIsModalOpen(true);
    }
    handleClientContactDetailsChange('selectedMobileNumberOwner', selectedMobileOwnerOption);
  };

  const handleAlternativeNumberOwnerChange = (selectedAlternativeOwnerOption) => {
    // eslint-disable-next-line quotes
    if (selectedAlternativeOwnerOption.label === "Someone Else's Number") {
      setRelation({
        relationMobile: clientContactDetails.alternativeNumber,
      });
      setClientRelationInformation({
        ...clientRelationInformation,
        relationFirstName: '',
        relationLastName: '',
        relationship: '',
        relationMobile: clientContactDetails?.alternativeNumber || '',
        relationMobileNumberVerified: false,
        selectedDiscretion: '',
        otherSpecify: '',
        relationEmail: '',
        emailVerified: false,
        isClientMinor: false,
        isLegalGuardian: false,
        isMainContact: false,
      });

      setIsModalOpen(true);
    }
    handleClientContactDetailsChange('selectedAlternativeNumberOwner', selectedAlternativeOwnerOption);
  };

  const updateSearchResults = (relationsList) => {
    closeModal();
    handleClientRelationInformationChange('searchResults', [...relationsList]);
  };

  const closeModal = () => {
    setClientRelationError({});
    setIsModalOpen(false);
  };

  const handleCheckboxChange = (checkboxName) => {
    switch (checkboxName) {
      case 'Appointment':
        handleClientContactDetailsChange('isAppointment', true);
        break;
      case 'Study Appointment':
        handleClientContactDetailsChange('isStudyAppointment', true);
        break;
      case 'Friend':
        handleClientContactDetailsChange('isFriend', true);
        break;
      // eslint-disable-next-line quotes
      case "Doctor's Appointment":
        handleClientContactDetailsChange('isDoctorsAppointment', true);
        break;
      case 'Aurum':
        handleClientContactDetailsChange('isAurum', true);
        break;
      case 'Other':
        handleClientContactDetailsChange('isOther', true);
        break;
      default:
        break;
    }
  };

  return (
    <div className="tab-container">
      <div className="client-contact-details">
        <div className="client-contact-details-input-pair">
          <div>
            <div className="input-container">
              <label className={'input-label'}>Security Question:</label>
              <Select
                options={securityQuestionList.map((origin) => ({
                  value: origin,
                  label: origin.question,
                }))}
                closeMenuOnSelect={true}
                onChange={handleSecurityQuestionChange}
                isDisabled={fieldsDisabled}
                value={clientContactDetails.selectedSecurityQuestion || null}
                className="input-select"
                classNamePrefix={`input-select${clientContactDetailsErrors.securityQuestionError && 'error-border'}`}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'clientSecurityQuestion123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Security Question:'}
                />
              )}
            </div>
            <div className="error-message">
              {clientContactDetailsErrors.securityQuestionError && (
                <div className="small-red-text">{clientContactDetailsErrors.securityQuestionError}</div>
              )}
            </div>
          </div>

          <div>
            <div className="input-container">
              <label className={'input-label'}>Security Answer:</label>
              <input
                className={`text-input ${clientContactDetailsErrors.securityAnswerError && 'error-border'}`}
                type="text"
                value={clientContactDetails.securityAnswer}
                disabled={fieldsDisabled}
                onChange={({ target }) => {
                  handleClientContactDetailsChange('securityAnswer', target.value);
                }}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'clientSecurityAnswer123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Security Answer:'}
                />
              )}
            </div>
            <div className="error-message">
              {clientContactDetailsErrors.securityAnswerError && (
                <div className="small-red-text">{clientContactDetailsErrors.securityAnswerError}</div>
              )}
            </div>
          </div>
        </div>

        <div className="client-contact-details-input-pair">
          <div>
            <div className="input-container">
              <label className={'input-label'}>Mobile Number:</label>
              <input
                className={`text-input ${clientContactDetailsErrors.mobileNumberError && 'error-border'}`}
                type="text"
                value={clientContactDetails.mobileNumber}
                disabled={fieldsDisabled}
                onChange={({ target }) => {
                  handleClientContactDetailsChange('mobileNumber', target.value);
                }}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'clientMobileNumber123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Mobile Number:'}
                />
              )}
            </div>
            <div className="error-message">
              {clientContactDetailsErrors.mobileNumberError && (
                <div className="small-red-text">{clientContactDetailsErrors.mobileNumberError}</div>
              )}
            </div>
          </div>

          <div>
            <div className="input-container">
              <label className={'input-label'}>Whose Mobile Number:</label>
              <Select
                options={mobileNumberList.current.map((owner) => ({
                  value: owner,
                  label: owner,
                }))}
                closeMenuOnSelect={true}
                onChange={handleMobileNumberOwnerChange}
                value={clientContactDetails.selectedMobileNumberOwner || null}
                className="input-select"
                classNamePrefix={`input-select${clientContactDetailsErrors.mobileNumberOwnerError && 'error-border'}`}
                isDisabled={fieldsDisabled}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'mobileOwner123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Whose Mobile Number:'}
                />
              )}
            </div>
            <div className="error-message">
              {clientContactDetailsErrors.mobileNumberOwnerError && (
                <div className="small-red-text">{clientContactDetailsErrors.mobileNumberOwnerError}</div>
              )}
            </div>
          </div>
        </div>

        <div className="client-contact-details-input-pair">
          <div>
            <div className="input-container">
              <label className={'input-label'}>Verified:</label>
              <Checkbox
                color={'#000000'}
                label={'Yes'}
                checked={clientContactDetails.mobileNumberVerified}
                fillStyle={'fill'}
                disabled={fieldsDisabled}
                onChange={() => handleClientContactDetailsChange('mobileNumberVerified', true)}
              />
              <Checkbox
                color={'#000000'}
                label={'No'}
                checked={!clientContactDetails.mobileNumberVerified}
                fillStyle={'fill'}
                disabled={fieldsDisabled}
                onChange={() => handleClientContactDetailsChange('mobileNumberVerified', false)}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'clientMobileNumberVerified123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Mobile Number Verified:'}
                />
              )}
            </div>
          </div>
          <div />
        </div>

        <div className="client-contact-details-input-pair">
          <div>
            <div className="input-container">
              <label className={'input-label'}>Alternative Number:</label>
              <input
                className={`text-input ${clientContactDetailsErrors.alternativeNumberError && 'error-border'}`}
                type="text"
                value={clientContactDetails.alternativeNumber}
                disabled={fieldsDisabled}
                onChange={({ target }) => {
                  handleClientContactDetailsChange('alternativeNumber', target.value);
                }}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'clientAlternativeNumber123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Alternative Number:'}
                />
              )}
            </div>
            <div className="error-message">
              {clientContactDetailsErrors.alternativeNumberError && (
                <div className="small-red-text">{clientContactDetailsErrors.alternativeNumberError}</div>
              )}
            </div>
          </div>
          <div>
            <div className="input-container">
              <label className={'input-label'}>Whose Mobile Number:</label>
              <Select
                options={alternativeNumberList.current.map((owner) => ({
                  value: owner,
                  label: owner,
                }))}
                closeMenuOnSelect={true}
                onChange={handleAlternativeNumberOwnerChange}
                value={clientContactDetails.selectedAlternativeNumberOwner || null}
                className="input-select"
                classNamePrefix={`input-select${
                  clientContactDetailsErrors.alternativeNumberOwnerError && 'error-border'
                }`}
                isDisabled={fieldsDisabled}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'alternativeOwner123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  oaster={showToaster}
                  fieldName={'Whose Mobile Number:'}
                />
              )}
            </div>
            <div className="error-message">
              {clientContactDetailsErrors.alternativeNumberOwnerError && (
                <div className="small-red-text">{clientContactDetailsErrors.alternativeNumberOwnerError}</div>
              )}
            </div>
          </div>
        </div>

        <div className="client-contact-details-input-pair">
          <div>
            <div className="input-container">
              <label className={'input-label'}>Verified:</label>
              <Checkbox
                color={'#000000'}
                label={'Yes'}
                checked={clientContactDetails.alternativeNumberVerified}
                disabled={fieldsDisabled}
                fillStyle={'fill'}
                onChange={() => handleClientContactDetailsChange('alternativeNumberVerified', true)}
              />
              <Checkbox
                color={'#000000'}
                label={'No'}
                checked={!clientContactDetails.alternativeNumberVerified}
                disabled={fieldsDisabled}
                fillStyle={'fill'}
                onChange={() => handleClientContactDetailsChange('alternativeNumberVerified', false)}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'clientAlternativeNumberVerified123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Alternate Number Verified:'}
                />
              )}
            </div>
          </div>
          <div />
        </div>
      </div>

      <div className="client-contact-details-checkbox-container">
        <label className={'input-label'}>What must we say when we call?</label>
        <div className="client-contact-details-checkbox-grid">
          <Checkbox
            checked={clientContactDetails.isAppointment}
            fillStyle={'fill'}
            color={'#000000'}
            label={'Appointment'}
            disabled={fieldsDisabled}
            onChange={() => handleCheckboxChange('Appointment')}
          />

          <Checkbox
            checked={clientContactDetails.isStudyAppointment}
            fillStyle={'fill'}
            color={'#000000'}
            label={'Study Appointment'}
            disabled={fieldsDisabled}
            onChange={() => handleCheckboxChange('Study Appointment')}
          />

          <Checkbox
            checked={clientContactDetails.isFriend}
            fillStyle={'fill'}
            color={'#000000'}
            label={'Friend'}
            disabled={fieldsDisabled}
            onChange={() => handleCheckboxChange('Friend')}
          />

          <Checkbox
            checked={clientContactDetails.isDoctorsAppointment}
            fillStyle={'fill'}
            color={'#000000'}
            // eslint-disable-next-line quotes
            label={"Doctor's Appointment"}
            disabled={fieldsDisabled}
            // eslint-disable-next-line quotes
            onChange={() => handleCheckboxChange("Doctor's Appointment")}
          />
          <Checkbox
            checked={clientContactDetails.isAurum}
            fillStyle={'fill'}
            color={'#000000'}
            label={'Aurum'}
            disabled={fieldsDisabled}
            onChange={() => handleCheckboxChange('Aurum')}
          />

          <div className="client-contact-details-other-specifiy">
            <Checkbox
              checked={clientContactDetails.isOther}
              fillStyle={'fill'}
              color={'#000000'}
              label={'Other, Specify:'}
              disabled={fieldsDisabled}
              onChange={() => handleCheckboxChange('Other')}
            />
            <input
              className={`text-input ${clientContactDetailsErrors.otherSpecifyError && 'error-border'}`}
              type="text"
              style={{ marginLeft: '5px' }}
              value={clientContactDetails.otherSpecify}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                handleClientContactDetailsChange('otherSpecify', target.value);
              }}
            />
          </div>
        </div>
      </div>
      <div className="contact-details-error-message">
        {clientContactDetailsErrors.otherSpecifyError && (
          <div className="small-red-text">{clientContactDetailsErrors.otherSpecifyError}</div>
        )}
      </div>

      <div className="client-contact-details-input-pair client-contact-details-email-container">
        <div>
          <div className="input-container">
            <label className={'input-label'}>Email Address:</label>
            <input
              className={`text-input ${clientContactDetailsErrors.emailError && 'error-border'}`}
              type="text"
              value={clientContactDetails.emailAddress}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                handleClientContactDetailsChange('emailAddress', target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientEmailAddress123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Email Address:'}
              />
            )}
          </div>
          <div className="error-message-review">
            {clientContactDetailsErrors.emailError && (
              <div className="small-red-text">{clientContactDetailsErrors.emailError}</div>
            )}
          </div>
        </div>

        <div>
          <div className="input-container">
            <label className={'input-label'}>Verified:</label>
            <Checkbox
              checked={clientContactDetails.emailAddressVerified}
              fillStyle={'fill'}
              color={'#000000'}
              label={'Yes'}
              disabled={fieldsDisabled}
              onChange={() => {
                handleClientContactDetailsChange('emailAddressVerified', true);
              }}
            />
            <Checkbox
              checked={!clientContactDetails.emailAddressVerified}
              fillStyle={'fill'}
              color={'#000000'}
              label={'No'}
              disabled={fieldsDisabled}
              onChange={() => {
                handleClientContactDetailsChange('emailAddressVerified', false);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientEmailAddressVerified123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Email Address Verified:'}
              />
            )}
          </div>
        </div>
      </div>

      <div className="input-container contact-details-input-text-area">
        <label className={'input-area-label'}>Confidentiality Instructions:</label>
        <textarea
          className="input-area"
          spellCheck={true}
          type="text"
          value={clientContactDetails.confidentialityInstructions}
          disabled={fieldsDisabled}
          onChange={({ target }) => {
            handleClientContactDetailsChange('confidentialityInstructions', target.value);
          }}
        />
        {toolTipsEnabled && (
          <ToolTip
            tooltipExternalReference={'confidentialityInstructions123'}
            context={context}
            setSpinnerText={setSpinnerText}
            showToaster={showToaster}
            fieldName={'Confidentiality Instructions:'}
          />
        )}
      </div>

      {isModalOpen && (
        <Modal
          closeModal={closeModal}
          setIsModalOpen={setIsModalOpen}
          headerTitle={'Add Relation'}
          buttonTitle={'Save'}
          width={'550px'}
          height={'max-content'}
          contentPadding={'2px'}
          justifyContent={'center'}
          hideButton={true}
        >
          <ClientRelations
            relation={relation}
            relations={clientRelationInformation.searchResults}
            context={context}
            setContext={setContext}
            closeModal={closeModal}
            discretionList={discretionList}
            updateSearchResults={updateSearchResults}
            clientRelationInformation={clientRelationInformation}
            setClientRelationInformation={setClientRelationInformation}
            clientRelationError={clientRelationError}
            setClientRelationError={setClientRelationError}
            handleClientRelationInformationChange={handleClientRelationInformationChange}
            handleClientRelationErrors={handleClientRelationErrors}
            fieldsDisabled={fieldsDisabled}
            disableMobileInput
          />
        </Modal>
      )}
    </div>
  );
};
